<script setup lang="ts">
import { computed } from 'vue'
import type { TourRect } from '../types/tour.types'
import { TOUR_CONFIG } from '../config/tour.config'

const props = defineProps<{ rect: TourRect | null }>()

// A single element positioned over the target draws the dim via a huge box-shadow
// spread and a rounded "hole". pointer-events stay off so the highlighted element
// (and everything else) remains fully interactive — the dim is purely visual.
const holeStyle = computed(() => {
  const r = props.rect
  if (!r) return undefined
  return {
    top: `${r.top}px`,
    left: `${r.left}px`,
    width: `${r.width}px`,
    height: `${r.height}px`,
    borderRadius: `${r.radius}px`,
  }
})
</script>

<template>
  <!-- No target → plain full-screen dim (centered modal step). -->
  <div v-if="!rect" class="tour-spotlight tour-spotlight--full" :style="{ zIndex: TOUR_CONFIG.zIndex.spotlight }" />

  <div
    v-else
    class="tour-spotlight tour-spotlight--hole"
    :style="{ ...holeStyle, zIndex: TOUR_CONFIG.zIndex.spotlight }"
  >
    <span class="tour-spotlight__ring" />
  </div>
</template>

<style scoped lang="scss">
.tour-spotlight {
  position: fixed;
  pointer-events: none;

  &--full {
    inset: 0;
    background: rgba(10, 20, 32, 0.62);
    animation: tour-fade-in 250ms ease both;
  }

  &--hole {
    box-shadow: 0 0 0 9999px rgba(10, 20, 32, 0.62);
    transition:
      top 260ms cubic-bezier(0.16, 1, 0.3, 1),
      left 260ms cubic-bezier(0.16, 1, 0.3, 1),
      width 260ms cubic-bezier(0.16, 1, 0.3, 1),
      height 260ms cubic-bezier(0.16, 1, 0.3, 1),
      border-radius 260ms ease;
  }

  &__ring {
    position: absolute;
    inset: -3px;
    border-radius: inherit;
    border: 2px solid rgba(70, 200, 222, 0.9);
    box-shadow: 0 0 0 4px rgba(70, 200, 222, 0.25);
    animation: tour-ring-pulse 2s ease-in-out infinite;
  }
}

@keyframes tour-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes tour-ring-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 4px rgba(70, 200, 222, 0.28);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(70, 200, 222, 0.1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .tour-spotlight--hole {
    transition: none;
  }
  .tour-spotlight__ring,
  .tour-spotlight--full {
    animation: none;
  }
}
</style>
